import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ICompany } from 'src/app/core/models/i-company';
import { IPerson } from 'src/app/core/models/i-person';
import { ITag } from 'src/app/core/models/i-tag';
import { ITopic } from 'src/app/core/models/i-topic';
import { compareArrays } from '../utils/array-utils';
import {
  ISelectListItem,
  SelectListItem,
} from 'src/app/shared-form-sections/interfaces/i-select-list-item';
import { IAssetClass } from 'src/app/core/models/i-asset-class';
import { IEtf } from 'src/app/core/models/i-etf';
import { IFund } from 'src/app/core/models/i-fund';
import { IInvestmentTrust } from 'src/app/core/models/i-investment-trust';
import { IManager } from 'src/app/core/models/i-manager';
import { ISector } from 'src/app/core/models/i-sector';
import { IShare } from 'src/app/core/models/i-share';
import { SurtitleTagType } from 'src/app/content-management/shared/enums/surtitle-tag-type';
import { ISurtitle } from 'src/app/core/models/i-surtitle';

type WithSurtitlesFormSectionType = {
  _initSurtitles(
    surtitles: ISurtitle[],
    tags: ITag[],
    topics: ITopic[],
    companies: ICompany[],
    people: IPerson[],
    managers?: IManager[],
    funds?: IFund[],
    sectors?: ISector[],
    assetClasses?: IAssetClass[],
    etfs?: IEtf[],
    investmentTrusts?: IInvestmentTrust[],
    shares?: IShare[]
  ): void;
  _initSurtitlesValueChanges(
    getPreviousSurtitlesValue: () => ISurtitle[] | null
  ): void;

  _buildSurtitles(): ISurtitle[];

  _findEditionLanguage(selectedCodes: string[]): void;

  get surtitles(): ISurtitle[];
  get tags(): ITag[];
  get topics(): ITopic[];
  get companies(): ICompany[];
  get people(): IPerson[];
};

type SurtitlesFormSectionRequerimentsBase = {
  form: FormGroup;
  FORM_PATHS: {
    SURTITLES: () => string;
    EDITION_LANGUAGE: () => string;
    TYPED_TAGS: () => string;
    TAGS: () => string;
    TOPICS: () => string;
    COMPANIES: () => string;
    PEOPLE: () => string;
  };
  destroy$: Subject<boolean>;
};

export function WithSurtitlesFormSection<
  TBase extends Constructor<SurtitlesFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithSurtitlesFormSectionType;
  prototype: WithSurtitlesFormSectionType;
} & TBase {
  return class WithSurtitlesFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initSurtitles(
      surtitles: ISurtitle[],
      tags: ITag[],
      topics: ITopic[],
      companies: ICompany[],
      people: IPerson[],
      managers: IManager[],
      funds: IFund[],
      sectors: ISector[],
      assetClasses: IAssetClass[],
      etfs: IEtf[],
      investmentTrusts: IInvestmentTrust[],
      shares: IShare[]
    ): void {
      const existingTagsControl = this.form.get(
        this.FORM_PATHS.TYPED_TAGS()
      ) as FormArray;

      if (surtitles) {
        const surtitlesControl = this.form.get(this.FORM_PATHS.SURTITLES());
        surtitles.forEach((i) => {
          (surtitlesControl as FormArray).push(new FormControl(i));
        });
      }

      if (tags) {
        tags.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(i.id, i.text);
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Soft })
          );
        });
      }

      if (topics) {
        topics.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.displayName
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Topic })
          );
        });
      }

      if (companies) {
        companies.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Company })
          );
        });
      }

      if (people) {
        people.forEach((i) => {
          let displayText: string | undefined;

          if ('text' in i && typeof i.text === 'string') {
            displayText = i.text;
          } else if ('name' in i && typeof i.name === 'string') {
            displayText = i.name;
          }
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            displayText
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Person })
          );
        });
      }

      if (managers) {
        managers.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.firstName + ' ' + i.lastName
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Manager })
          );
        });
      }

      if (funds) {
        funds.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Fund })
          );
        });
      }

      if (sectors) {
        sectors.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Sector })
          );
        });
      }

      if (assetClasses) {
        assetClasses.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.AssetClass })
          );
        });
      }

      if (etfs) {
        etfs.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Fund })
          );
        });
      }

      if (investmentTrusts) {
        investmentTrusts.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.name
          );
          existingTagsControl.push(
            new FormControl({
              tag: tag,
              tagType: SurtitleTagType.InvestmentTrust,
            })
          );
        });
      }

      if (shares) {
        shares.forEach((i) => {
          const tag: ISelectListItem = new SelectListItem(
            i.id.toString(),
            i.companyName
          );
          existingTagsControl.push(
            new FormControl({ tag: tag, tagType: SurtitleTagType.Share })
          );
        });
      }
    }

    _initSurtitlesValueChanges(
      getPreviousSurtitlesValue: () => ISurtitle[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.SURTITLES())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            const surtitles: ISurtitle[] =
              this.form.get(this.FORM_PATHS.SURTITLES()).value || [];
            const previousValue = getPreviousSurtitlesValue();
            if (newValue == null || previousValue == null) {
              if (newValue === previousValue) {
                this.form.get(this.FORM_PATHS.SURTITLES()).markAsPristine();
              } else {
                this.form.get(this.FORM_PATHS.SURTITLES()).markAsDirty();
              }
              return;
            }
            if (compareArrays(newValue, previousValue)) {
              this.form.get(this.FORM_PATHS.SURTITLES()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.SURTITLES()).markAsDirty();
            }
          },
        });
    }

    _buildSurtitles(): ISurtitle[] {
      const surtitles: ISurtitle[] =
        this.form.get(this.FORM_PATHS.SURTITLES()).value || [];

      return surtitles.map((s) => ({
        ...s,
        displayText:
          s.displayText && s.displayText.trim().length > 0
            ? s.displayText
            : s.name,
      }));
    }

    _findEditionLanguage(selectedCodes: string[]) {
      const codeToLang = new Map<string, string>([
        ['web3__sections__international_germany', 'de'],
        ['web3__sections__international_austria', 'de'],
        ['web3__sections__international_italy', 'it'],
        ['web3__sections__international_france', 'fr'],
        ['web3__sections__international_spain', 'es'],
      ]);
      let lang = 'en';
      if (selectedCodes.length) {
        const langs = selectedCodes
          .map((c) => codeToLang.get(c))
          .filter(Boolean) as string[];
        if (
          langs.length === selectedCodes.length &&
          new Set(langs).size === 1
        ) {
          lang = langs[0];
        }
      }
      this.form.get(this.FORM_PATHS.EDITION_LANGUAGE()).setValue(lang);
    }

    get surtitles(): ISurtitle[] {
      return this.form.get(this.FORM_PATHS.SURTITLES()).value;
    }

    get tags(): ITag[] {
      return this.form.get(this.FORM_PATHS.TAGS()).value;
    }

    get topics(): ITopic[] {
      return this.form.get(this.FORM_PATHS.TOPICS()).value;
    }

    get companies(): ICompany[] {
      return this.form.get(this.FORM_PATHS.COMPANIES()).value;
    }

    get people(): IPerson[] {
      return this.form.get(this.FORM_PATHS.PEOPLE()).value;
    }
  };
}
